<template>
  <div class="main-container" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
    <!-- 插件详情卡片 -->
    <el-card class="plugin-card" v-if="pluginName" shadow="hover">
      <div class="plugin-info">
        <div class="plugin-info-item">
          <span class="info-label">插件名称：</span>
          <span class="info-value">{{ pluginName }}</span>
        </div>
        <div class="plugin-info-item">
          <span class="info-label">创建时间：</span>
          <span class="info-value">{{ createdTime }}</span>
        </div>
        <div class="plugin-info-item">
          <span class="info-label">版本数量：</span>
          <span class="info-value">{{ versions.length }}</span>
        </div>
        <div class="plugin-info-item">
          <span class="info-label">到期时间：</span>
          <span class="info-value">{{ renewal }}</span>
        </div>
      </div>
      <el-button type="primary" class="create-btn" @click="dialogVisible = true">
        <i class="el-icon-plus"></i>
        创建版本
      </el-button>
    </el-card>

    <!-- 创建版本弹窗 -->
    <el-dialog
        title="创建版本"
        :visible.sync="dialogVisible"
        width="500px"
        :modal-append-to-body="false"
        custom-class="center-dialog"
        :close-on-click-modal="false">
      <el-form :model="formData" ref="formRef" label-width="100px" class="dialog-form">
        <el-form-item label="版本名称" prop="versionName">
          <el-input 
              v-model="formData.versionName" 
              placeholder="请输入版本名称"
              class="form-input"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" class="cancel-btn">取 消</el-button>
        <el-button type="primary" @click="createVersion" :loading="creating" class="confirm-btn">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 版本列表表格 -->
    <el-card class="table-card" shadow="hover" v-if="pluginName">
      <template slot="header">
        <div class="table-header">
          <span>版本列表</span>
          <span class="version-count" v-if="versions.length > 0">{{ versions.length }} 个版本</span>
        </div>
      </template>
      <el-table
          :data="versions"
          style="width: 100%"
          v-if="versions.length > 0"
          stripe
          border
          :row-class-name="tableRowClassName"
      >
        <el-table-column
            prop="versionName"
            label="版本"
            width="180"
            align="center"
        >
          <template v-slot:default="scope">
            <span class="version-name">{{ scope.row.versionName }}</span>
          </template>
        </el-table-column>
        <el-table-column
            prop="createTime"
            label="创建时间"
            :formatter="formatTime"
            width="180"
            align="center"
        ></el-table-column>
        <el-table-column
            prop="updateTime"
            :formatter="formatTime"
            label="修改时间"
            align="center"
        ></el-table-column>
        <el-table-column label="上传插件" width="150" align="center">
          <template v-slot:default="scope">
            <el-upload
                :ref="'upload-' + scope.row.id"
                class="upload-demo"
                action="/file/upload"
                :before-upload="(file) => beforeUpload(file, scope.row)"
                :on-success="(response, file) => handleUploadSuccess(response, file, scope.row)"
                :on-error="(err, file, fileList) => handleUploadError(err, file, fileList, scope.row)"
                :show-file-list="false"
                :auto-upload="true"
                :http-request="(params) => customUpload(params, scope.row)">
              <el-button
                  type="primary"
                  size="small"
                  :loading="scope.row.uploading"
                  class="action-btn upload-btn"
              >
                <i class="el-icon-upload"></i>
                上传
              </el-button>
            </el-upload>
          </template>
        </el-table-column>
        <el-table-column label="下载插件" width="120" align="center">
          <template v-slot:default="scope">
            <el-button
                type="success"
                size="small"
                @click="download(scope.row)"
                :disabled="scope.row.upload !== 1"
                class="action-btn download-btn"
            >
              <i class="el-icon-download"></i>
              下载
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="删除版本" width="120" align="center">
          <template v-slot:default="scope">
            <el-button
                type="danger"
                size="small"
                @click="deleteVersion(scope.row)"
                class="action-btn delete-btn"
            >
              <i class="el-icon-delete"></i>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-else-if="!loading" class="empty-tip">
        <el-empty
            description="暂无版本数据"
            :image-size="120"
        >
          <el-button type="primary" @click="dialogVisible = true">
            <i class="el-icon-plus"></i>
            创建版本
          </el-button>
        </el-empty>
      </div>
    </el-card>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: "MyMain",
  props: {
    selectedPlugin: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      versions: [],
      pluginName: "",
      createdTime: "",
      renewal: "",
      loading: false,
      creating: false,
      dialogVisible: false,
      formData: {
        versionName: "",
        pid: null
      }
    }
  },
  watch: {
    selectedPlugin: {
      handler(newPlugin, oldPlugin) {
        if (newPlugin && (!oldPlugin || newPlugin.id !== oldPlugin.id)) {
          this.loadVersionList(newPlugin)
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    tableRowClassName({rowIndex}) {
      return rowIndex % 2 === 0 ? 'even-row' : 'odd-row';
    },
    formatTime(row, column, cellValue) {
      if (!cellValue) return ''
      return dayjs(cellValue).format('YYYY-MM-DD HH:mm:ss')
    },

    async loadVersionList(plugin) {
      let currentPlugin = plugin || this.selectedPlugin

      if (!currentPlugin) {
        const pluginStr = localStorage.getItem('selectPlugin')
        if (pluginStr) {
          try {
            currentPlugin = JSON.parse(pluginStr)
          } catch (e) {
            console.error('解析插件信息失败', e)
          }
        }
      }

      if (!currentPlugin || !currentPlugin.id) {
        console.warn('未选中任何插件')
        this.versions = []
        this.pluginName = ""
        this.createdTime = ""
        this.renewal = ""
        return
      }

      this.formData.pid = currentPlugin.id
      this.loading = true

      try {
        const response = await this.$axios({
          method: 'get',
          url: '/version/getList',
          params: {
            pid: currentPlugin.id
          }
        })

        if (response.data.code === "200" || response.data.success) {
          const versionList = response.data.data.versionList || []
          this.versions = versionList.map(v => ({
            ...v,
            uploading: false
          }))
          this.versions = versionList.sort((a, b) => {
            return new Date(a.createTime) - new Date(b.createTime)
          })
          this.pluginName = currentPlugin.pluginName || ''
          this.createdTime = currentPlugin.createTime ?
              dayjs(currentPlugin.createTime).format('YYYY-MM-DD HH:mm:ss') : ''
          this.renewal = currentPlugin.renewal ?
              dayjs(currentPlugin.renewal).format('YYYY-MM-DD HH:mm:ss') : ''
        } else if (response.data.code === "203") {
          this.$message.warning(response.data.data?.message || '请求失败')
          this.versions = []
        } else {
          this.$message.error('加载版本列表失败')
        }
      } catch (error) {
        console.error('加载版本列表失败:', error)
        this.$message.error('网络错误，请稍后重试')
        this.versions = []
      } finally {
        this.loading = false
      }
    },

    // 上传前的验证
    beforeUpload(file, version) {
      console.log('准备上传文件:', file.name, '版本:', version.versionName)

      // 验证文件格式
      const isJar = file.name.endsWith('.jar')
      if (!isJar) {
        this.$message.error('只能上传 .jar 文件!')
        return false
      }

      // 验证文件大小（100MB）
      const isLt100M = file.size / 1024 / 1024 < 100
      if (!isLt100M) {
        this.$message.error('文件大小不能超过 100MB!')
        return false
      }

      // 设置上传状态
      version.uploading = true

      return true
    },

    // 自定义上传方法（核心修复）
    // 自定义上传方法（修复版）
    async customUpload(params, version) {
      const { file, onProgress, onSuccess, onError } = params

      const formData = new FormData()
      formData.append('file', file)
      formData.append('pluginName', this.pluginName)
      formData.append('versionName', version.versionName)
      formData.append('versionId', version.id)

      try {
        const response = await this.$axios({
          method: 'post',
          url: '/file/upload',
          data: formData,
          timeout: 300000, // 5分钟超时
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress({ percent: percentCompleted })
            }
          }
        })

        // 关键修复：直接传递响应数据，并确保响应格式正确
        console.log('上传响应:', response.data)

        // 检查响应是否成功
        if (response.data.code === '200') {
          // 上传成功，传递响应数据
          onSuccess(response.data, file)
        } else {
          // 上传失败（后端返回错误）
          onError(new Error(response.data?.data.message || '上传失败'))
        }

      } catch (error) {
        console.error('自定义上传失败:', error)

        let errorMessage = '上传失败'
        if (error.code === 'ECONNABORTED') {
          errorMessage = '上传超时，请检查网络后重试'
        } else if (error.response) {
          errorMessage = error.response.data?.message || '服务器错误'
        } else if (error.message && error.message.includes('Network Error')) {
          errorMessage = '网络连接失败，请检查网络后重试'
        }

        // 调用上传失败回调
        onError(new Error(errorMessage))
      }
    },

    // 上传成功回调（修复版）
    handleUploadSuccess(response, file, version) {
      console.log('上传成功响应:', response)
      console.log('响应类型:', typeof response)
      console.log('响应code:', response?.code)
      if (response === undefined){
        return
      }
      // 重置上传状态
      version.uploading = false

      // 兼容不同的响应格式
      const isSuccess = response && (response.code === "200" || response.code === 200 || response.success === true)

      if (isSuccess) {
        this.$message.success('上传成功')
        // 更新版本的上传状态
        this.$set(version, 'upload', 1)
        // 刷新列表以更新显示
        this.refresh()
      } else {
        const errorMsg = response?.data.message || response?.data?.message || '上传失败'
        this.$message.error(errorMsg)
      }

      // 清空上传组件，允许重新上传
      const uploadRef = this.$refs['upload-' + version.id]
      if (uploadRef && uploadRef[0] && uploadRef[0].clearFiles) {
        uploadRef[0].clearFiles()
      }
    },

    // 上传失败回调（增强版）
    handleUploadError(err, file, fileList, version) {
      console.error('上传失败:', err)
      console.error('错误详情:', err.message)

      // 重置上传状态
      if (version) {
        version.uploading = false
      } else {
        // 如果没找到版本，遍历所有版本重置状态
        this.versions.forEach(v => {
          v.uploading = false
        })
      }

      // 获取错误信息
      let errorMsg = '上传失败，请检查网络后重试'
      if (err.message) {
        errorMsg = err.message
      } else if (typeof err === 'string') {
        errorMsg = err
      }

      this.$message.error(errorMsg)

      // 清空上传组件
      if (version) {
        const uploadRef = this.$refs['upload-' + version.id]
        if (uploadRef && uploadRef[0] && uploadRef[0].clearFiles) {
          uploadRef[0].clearFiles()
        }
      }
    },

    async download(version) {
      if (version.upload !== 1) {
        this.$message.warning("该版本还未上传插件文件!")
        return
      }

      const loadingInstance = this.$loading({
        lock: true,
        text: '正在下载...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const response = await this.$axios({
          method: 'get',
          url: '/file/getPlugins',
          params: {
            name: this.pluginName,
            version: version.versionName
          },
          responseType: 'blob',
          timeout: 60000
        })

        // 检查响应类型
        if (response.data.type === 'application/json') {
          const text = await this.readBlobAsText(response.data)
          try {
            const errorData = JSON.parse(text)
            this.$message.error(errorData.message || '下载失败')
          } catch (e) {
            this.$message.error('下载失败')
          }
          return
        }

        // 创建下载链接
        const blob = new Blob([response.data])
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `${this.pluginName}_${version.versionName}.jar`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)

        this.$message.success("下载成功")
      } catch (error) {
        console.error('下载错误:', error)
        if (error.code === 'ECONNABORTED') {
          this.$message.error("下载超时，请重试")
        } else {
          this.$message.error("下载失败: " + (error.message || '网络错误'))
        }
      } finally {
        loadingInstance.close()
      }
    },

    // 辅助方法：读取 Blob 为文本
    readBlobAsText(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.readAsText(blob)
      })
    },

    refresh() {
      this.loadVersionList(this.selectedPlugin)
    },

    createVersion() {
      if (!this.formData.versionName) {
        this.$message.warning('请输入版本名称！')
        return
      }

      if (!this.formData.pid) {
        this.$message.error('无法获取插件信息')
        return
      }

      this.creating = true

      this.$axios({
        method: 'post',
        url: '/version/create',
        data: {
          versionName: this.formData.versionName,
          pid: this.selectedPlugin.id
        },
        timeout: 30000
      }).then(response => {
        if (response.data.code === "200" || response.data.success) {
          this.$message.success('创建版本成功')
          this.dialogVisible = false
          this.formData.versionName = ''
          this.refresh()
        } else {
          this.$message.error(response.data.data.message || '创建失败')
        }
      }).catch(error => {
        console.error(error)
        this.$message.error('创建版本请求失败：' + (error.message || '网络错误'))
      }).finally(() => {
        this.creating = false
      })
    },

    deleteVersion(version) {
      this.$confirm(`确定要删除版本 "${version.versionName}" 吗？删除后将无法恢复！`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$axios({
          method: 'get',
          url: '/version/delete',
          params: {
            id: version.id,
            pluginName: this.pluginName,
            versionName: version.versionName
          }
        }).then(response => {
          if (response.data.code === "200" || response.data.success) {
            this.$message.success(response.data.data.message)
            window.location.reload();
          }
          if (response.data.code === "203") {
            this.$message.error(response.data.data.message)
          }
        }).catch(error => {
          alert("error" + error)
        }).finally(() => {
          this.loading = false
        })

        // TODO: 调用删除版本的接口
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.main-container {
  padding: 20px;
  min-height: 100%;
  background: #f5f7fa;
}

/* 插件信息卡片 */
.plugin-card {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.plugin-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.plugin-info {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
}

.plugin-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.create-btn {
  margin-left: auto;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.create-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 表格卡片 */
.table-card {
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.version-count {
  font-size: 14px;
  color: #999;
  font-weight: normal;
}

/* 表格样式 */
::v-deep .el-table {
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .el-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  height: 50px;
}

::v-deep .el-table td {
  height: 48px;
  vertical-align: middle;
}

::v-deep .el-table .even-row {
  background: #fafafa;
}

::v-deep .el-table .odd-row {
  background: white;
}

::v-deep .el-table tr:hover {
  background: #f5f7fa !important;
}

/* 版本名称 */
.version-name {
  font-weight: 500;
  color: #333;
}

/* 操作按钮 */
.action-btn {
  border-radius: 6px;
  padding: 4px 12px;
  transition: all 0.3s ease;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.upload-btn:hover {
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.download-btn:hover {
  box-shadow: 0 2px 8px rgba(103, 194, 58, 0.4);
}

.delete-btn:hover {
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.4);
}

/* 空状态 */
.empty-tip {
  padding: 60px 0;
  text-align: center;
}

/* 弹窗样式 */
.dialog-form {
  padding: 10px 0;
}

.form-input {
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
}

.cancel-btn, .confirm-btn {
  border-radius: 8px;
  padding: 8px 20px;
  transition: all 0.3s ease;
}

.confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-container {
    padding: 16px;
  }
  
  .plugin-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .create-btn {
    margin-left: 0;
    width: 100%;
    margin-top: 12px;
  }
  
  ::v-deep .el-table {
    font-size: 12px;
  }
  
  ::v-deep .el-table th,
  ::v-deep .el-table td {
    padding: 8px;
  }
  
  .action-btn {
    padding: 2px 8px;
    font-size: 10px;
  }
}
</style>

<style>
.center-dialog {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(100% - 30px);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.center-dialog .el-dialog__header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
}

.center-dialog .el-dialog__title {
  color: white;
  font-weight: 600;
}

.center-dialog .el-dialog__body {
  flex: 1;
  overflow: auto;
  padding: 24px;
}

.center-dialog .el-dialog__footer {
  padding: 20px;
  border-top: 1px solid #f0f0f0;
}
</style>